import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import AddNewEntryModal from "../components/AddNewEntryModal";
import Edit from "../assets/svgIcons/FormEdit.svg";
import { displayToast } from "../utils/Toast";
import { useErrorHandler } from "../services/ErrorHandler";
import { useSalesFormStore, useRestoredSalesForm } from "../services/useSalesFormStore";
import { fetchCustomerOrVendor } from "../apis/transaction";
import "../styles/components/BuyerDetails.css";

const BuyerDetails = ({
  tab,
  company,
  isEditMode,
  onCompanyDataChange,
  allowNavigation = true,
  customerType = "",
}) => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(company);
  const { handleErrorResponse } = useErrorHandler();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const setCustomerDetails = useSalesFormStore(
    (state) => state.setCustomerDetails
  );
  const formData = useSalesFormStore((state) => state.formData);
  const restoredSnapshot = useRestoredSalesForm();

  useEffect(() => {
    setCurrentCompany(company);
  }, [company]);

  const handleCompanyChange = (updatedCompany) => {
    setCurrentCompany(updatedCompany);
    if (onCompanyDataChange) {
      onCompanyDataChange(updatedCompany);
    }

    const initialDetails = {
      company_name: updatedCompany.display_name || updatedCompany.company_name,
      company_phone: updatedCompany.contact || updatedCompany.company_phone,
      company_address:
        updatedCompany.address || updatedCompany.delivery_address,
      contact_person_name: updatedCompany?.contact_person_name,
      company_gst_no:
        updatedCompany.gstno ||
        updatedCompany.gst_no ||
        updatedCompany.company_gst_no,
      pincode: updatedCompany.pincode,
      country: updatedCompany.country,
      delivery_address:
        updatedCompany.delivery_address || updatedCompany.address,
      company_id: updatedCompany.company_id ?? updatedCompany.tran_id,
    };
    setCustomerDetails(initialDetails);
  };

  useEffect(() => {
    if (restoredSnapshot && currentCompany === company) return;
    const initialDetails = {};
    if (currentCompany) {
      initialDetails.company_name =
        currentCompany.display_name || currentCompany.company_name;
      initialDetails.company_phone =
        currentCompany.contact || currentCompany.company_phone;
      initialDetails.company_address =
        currentCompany.company_address || currentCompany.address || currentCompany.delivery_address;
      initialDetails.contact_person_name = currentCompany.contact_person_name;
      initialDetails.company_gst_no =
        currentCompany.gstno ||
        currentCompany.gst_no ||
        currentCompany.company_gst_no;
      initialDetails.pincode = currentCompany.pincode;
      initialDetails.country = currentCompany.country;
      initialDetails.delivery_address =
        currentCompany.delivery_address || currentCompany.address;
      initialDetails.company_id = currentCompany.company_id ?? currentCompany.tran_id;
    }
    setCustomerDetails(initialDetails);
  }, [currentCompany, company, restoredSnapshot, setCustomerDetails]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmitModal = (formData) => {
    setCustomerDetails(formData);
    setIsModalOpen(false);
  };

  const handleAddNewItem = () => {
    setIsModalOpen(true);
  };

  const handleCompanySelect = (selectedCompany) => {
    if (allowNavigation) {
      navigate(`/${tab}/${category}/transactions`, {
        state: { company: selectedCompany },
      });
    } else {
      handleCompanyChange(selectedCompany);
      displayToast("Buyer details updated", "info");
    }
    setIsModalOpen(false);
  };

  const fetchCustomerOrVendorData = async () => {
    const type = "customer";
    const response = await fetchCustomerOrVendor({ type });

    if (response.description) {
      handleErrorResponse(response.description);
      displayToast(response.description, "error");
    } else {
      setCompanies(response.items);
    }
  };

  useEffect(() => {
    fetchCustomerOrVendorData();
  }, []);

  // Saved transactions only store a limited company_data snapshot (no
  // "state"), so once the master customer list loads, look up the matching
  // customer by company_id and merge in its state/GST for display + print.
  // This is NOT sent back to the backend on save (no matching DB column).
  useEffect(() => {
    if (!currentCompany || !companies.length) return;
    if (currentCompany.state) return;

    const masterId = currentCompany.company_id || currentCompany.tran_id;
    if (!masterId) return;

    const master = companies.find(
      (item) => Number(item.tran_id) === Number(masterId)
    );
    if (!master || !master.state) return;

    const enrichedCompany = {
      ...currentCompany,
      state: master.state,
      gst_no: currentCompany.gst_no || currentCompany.company_gst_no || master.gst_no,
    };
    setCurrentCompany(enrichedCompany);
    if (onCompanyDataChange) {
      onCompanyDataChange(enrichedCompany);
    }
  }, [companies, currentCompany]);

  return (
    <div className="buyer-details-container">
      <div className="buyer-details-header">
        <div className="buyer-details-header-title">
          <span className="buyer-details-header-icon">
            <User size={18} />
          </span>
          <h3>
            {customerType === "customer"
              ? "Customer Details"
              : "Supplier Details"}
          </h3>
        </div>
        {isEditMode && (
          <img
            src={Edit}
            className="buyer-edit-icon"
            onClick={handleAddNewItem}
          />
        )}
      </div>

      <div className="buyer-details-content">
        <div className="company-name">
          {currentCompany?.display_name || currentCompany?.company_name}
        </div>

        <div className="detail-row">
          <span className="label">Contact Person Name:</span>
          <span className="value underline">
            {currentCompany?.contact_person_name || "N/A"}
          </span>
        </div>

        <div className="detail-row">
          <span className="label">Phone:</span>
          <span className="value underline">
            {currentCompany?.contact || currentCompany?.company_phone}
          </span>
        </div>

        <div className="detail-row">
          <span className="label underline">Address:</span>
          <span className="value">
            {currentCompany?.address || currentCompany?.delivery_address}
          </span>
          <div className="location-details">
            {currentCompany?.country} {currentCompany?.pincode}
          </div>
        </div>

        <div className="detail-row">
          <span className="label">GSTIN:</span>
          <span className="value underline">
            {currentCompany?.gstno ||
              currentCompany?.gst_no ||
              currentCompany?.company_gst_no ||
              "N/A"}
          </span>
        </div>

        {/* Address and delivery address should be separated */}
        {/* <div className="delivery-section">
          <h4>Delivery Location</h4>
          <div className="detail-row">
            <span className="label underline">Address:</span>
            <span className="value">
              {currentCompany?.address || currentCompany?.delivery_address}
            </span>
            <div className="location-details">
              {currentCompany?.country} {currentCompany?.pincode}
            </div>
          </div>
        </div> */}
      </div>
      <AddNewEntryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        heading={`Please Select ${
          customerType === "vendor" ? "Supplier" : "Customer"
        } Company`}
        placeholder="Company Name"
        options={companies}
        onCompanySelect={handleCompanySelect}
        customerType={customerType}
        searchPlaceholder="Search Company name"
      />
    </div>
  );
};

export default BuyerDetails;
